import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Star, MapPin, Calendar, Award, UserPlus, Circle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const POPULAR_SPORTS = [
  { name: 'Football', emoji: '⚽', slug: 'Football' },
  { name: 'Cricket', emoji: '🏏', slug: 'Cricket' },
  { name: 'Basketball', emoji: '🏀', slug: 'Basketball' },
  { name: 'Tennis', emoji: '🎾', slug: 'Tennis' },
  { name: 'Swimming', emoji: '🏊', slug: 'Swimming' },
  { name: 'Boxing', emoji: '🥊', slug: 'Boxing' }
];

const WHY_DIOS = [
  {
    icon: '🎯',
    title: 'Expert coaches worldwide',
    desc: '101 sports, verified professionals'
  },
  {
    icon: '📅',
    title: 'Book your session instantly',
    desc: 'Live and recorded options available'
  },
  {
    icon: '🌍',
    title: 'Train from anywhere',
    desc: 'Local facilities or online sessions'
  }
];

function PremiumLandingPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [featuredCoaches, setFeaturedCoaches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedCoaches();
  }, []);

  const fetchFeaturedCoaches = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/coaches?featured=true&limit=3`);
      setFeaturedCoaches(response.data.coaches || []);
    } catch (error) {
      console.error('Error fetching coaches:', error);
      setFeaturedCoaches([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/courses?search=${encodeURIComponent(searchQuery)}`);
    } else {
      navigate('/courses');
    }
  };

  const handleSportClick = (sport) => {
    navigate(`/courses?sport=${encodeURIComponent(sport)}`);
  };

  return (
    <div className="min-h-screen bg-deep-dark">
      {/* Hero Section - Clean & Minimal */}
      <div className="relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 pt-24 pb-32 text-center">
          {/* Main Headline */}
          <h1 className="text-6xl md:text-7xl font-bold text-white mb-6 tracking-tight">
            Find your coach
          </h1>
          
          <p className="text-xl text-soft-lavender mb-12 max-w-2xl mx-auto">
            Master any sport with expert coaches. Book live sessions or learn at your own pace.
          </p>
          
          {/* Single Search Bar - Uber Style */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="What sport do you want to master?"
                className="w-full bg-dark-surface border border-premium-gold/30 rounded-2xl px-6 py-5 pr-14 text-white placeholder-soft-lavender text-lg focus:outline-none focus:border-premium-gold transition-colors"
              />
              <button
                onClick={handleSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-premium-gold rounded-xl flex items-center justify-center hover:opacity-90 transition-opacity"
              >
                <Search className="w-5 h-5 text-deep-dark" strokeWidth={1.5} />
              </button>
            </div>
          </div>
          
          {/* Popular Sport Pills */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
            {POPULAR_SPORTS.map(sport => (
              <button
                key={sport.slug}
                onClick={() => handleSportClick(sport.slug)}
                className="px-6 py-3 bg-dark-surface border border-premium-gold/30 rounded-full text-white hover:border-premium-gold transition-all flex items-center gap-3"
              >
                <span className="text-2xl" style={{filter: 'grayscale(100%) sepia(100%) hue-rotate(10deg) saturate(400%) brightness(1.2)'}}>{sport.emoji}</span>
                <span>{sport.name}</span>
              </button>
            ))}
          </div>
          
          {/* Single Primary CTA */}
          <button
            onClick={() => navigate('/courses')}
            className="px-8 py-4 bg-premium-gold text-deep-dark font-semibold rounded-xl text-lg hover:opacity-90 transition-opacity inline-block"
          >
            Find My Coach
          </button>
        </div>
      </div>

      {/* Featured Coaches Section */}
      <div className="max-w-7xl mx-auto px-4 py-20">
        <h2 className="text-3xl font-bold text-white mb-12 text-center">
          Featured Coaches
        </h2>
        
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-3 border-premium-gold/20 border-t-premium-gold"></div>
          </div>
        ) : featuredCoaches.length === 0 ? (
          // Empty State
          <div className="bg-dark-surface border border-premium-gold/20 rounded-2xl p-12 text-center">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-premium-gold/20 rounded-full flex items-center justify-center mb-4">
                <Award className="w-8 h-8 text-premium-gold" strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">
                Be the first to join as a coach
              </h3>
              <p className="text-soft-lavender mb-6">
                Share your expertise and inspire the next generation of athletes
              </p>
              <button
                onClick={() => navigate('/become-coach')}
                className="bg-transparent border border-premium-gold text-premium-gold hover:bg-premium-gold hover:text-deep-dark font-semibold py-3 px-6 rounded-lg transition-all flex items-center gap-2"
              >
                <UserPlus className="w-5 h-5" strokeWidth={1.5} />
                Apply as Coach
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredCoaches.map(coach => (
              <div 
                key={coach.coach_id}
                className="bg-dark-surface border border-premium-gold/20 rounded-2xl p-6 hover:border-premium-gold transition-all cursor-pointer"
                onClick={() => navigate(`/coaches/${coach.coach_id}`)}
              >
                <div className="flex flex-col items-center text-center">
                  {coach.photo ? (
                    <img
                      src={coach.photo}
                      alt={coach.first_name && coach.last_name ? `${coach.first_name} ${coach.last_name}` : coach.name}
                      className="w-24 h-24 rounded-full object-cover mb-4 border-2 border-premium-gold/30"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-premium-gold/20 flex items-center justify-center mb-4 border-2 border-premium-gold/30">
                      <Award className="w-12 h-12 text-premium-gold" strokeWidth={1.5} />
                    </div>
                  )}
                  <h3 className="text-xl font-semibold text-white mb-1">
                    {coach.first_name && coach.last_name 
                      ? `${coach.first_name} ${coach.last_name}`
                      : coach.name || 'Coach'}
                  </h3>
                  <p className="text-soft-lavender mb-3 capitalize">
                    {coach.specializations?.[0] || 'Multi-sport'}
                  </p>
                  
                  {coach.rating && coach.rating > 0 && (
                    <div className="flex items-center gap-2 mb-4">
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            strokeWidth={1.5}
                            className={`w-4 h-4 ${
                              i < Math.floor(coach.rating)
                                ? 'fill-premium-gold text-premium-gold'
                                : 'text-premium-gold/30'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-sm text-soft-lavender">
                        {coach.rating.toFixed(1)}
                      </span>
                    </div>
                  )}
                  
                  {coach.hourly_rate && (
                    <div className="text-2xl font-bold text-premium-gold mb-4">
                      ₹{coach.hourly_rate}/hour
                    </div>
                  )}
                  
                  <button className="w-full bg-premium-gold hover:opacity-90 text-deep-dark font-semibold py-2 rounded-lg transition-all">
                    Book Session
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Popular Sports Grid */}
      <div className="max-w-7xl mx-auto px-4 py-20">
        <h2 className="text-3xl font-bold text-white mb-12 text-center">
          Popular Sports
        </h2>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {POPULAR_SPORTS.map(sport => (
            <button
              key={sport.slug}
              onClick={() => handleSportClick(sport.slug)}
              className="bg-dark-surface border border-premium-gold/20 rounded-2xl p-8 hover:border-premium-gold transition-all group"
            >
              <div className="text-6xl mb-4 text-center" style={{filter: 'grayscale(100%) sepia(100%) hue-rotate(10deg) saturate(400%) brightness(1.2)'}}>
                {sport.emoji}
              </div>
              <div className="text-xl font-semibold text-white text-center group-hover:text-premium-gold transition-colors">
                {sport.name}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Why DIOS - Minimal */}
      <div className="max-w-5xl mx-auto px-4 py-20">
        <h2 className="text-3xl font-bold text-white mb-16 text-center">
          Why DIOS
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {WHY_DIOS.map((item, index) => (
            <div key={index} className="text-center">
              <div className="text-5xl mb-4">{item.icon}</div>
              <h3 className="text-xl font-semibold text-white mb-2">
                {item.title}
              </h3>
              <p className="text-soft-lavender">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-premium-gold/20 mt-20">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-black text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                D<span className="text-premium-gold">I</span>OS
              </span>
              <span className="text-xs text-soft-lavender ml-2" style={{ fontFamily: "'Barlow Condensed', sans-serif", fontStyle: 'italic', fontWeight: 900, color: '#C9A84C', letterSpacing: '3px' }}>
                TAP IN
              </span>
            </div>
            
            <div className="text-soft-lavender text-sm">
              © 2026 DIOS. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default PremiumLandingPage;
